import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  TextField
} from '@material-ui/core';
import { DatePickerRangeComponent } from 'src/components';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { transformerDateField } from 'src/utils';
import { AuditListPayload } from 'src/types';
import { ActionAuditFilter } from './ActionAuditFilter';
import { CustomKeyboardEvent } from 'src/types';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import { PopoverNotification } from 'src/components/popover';
import { useAudit } from 'src/hooks/audits';
import { useSearchParams } from 'react-router-dom';
import { Autocomplete } from '@material-ui/lab';

interface Props {
  onHandleAppliedFilter: (params: AuditListPayload) => void;
}

const Component: FC<Props> = ({ onHandleAppliedFilter }) => {
  const auditableIdRef = useRef<HTMLInputElement | null>(null);
  const [searchParams] = useSearchParams();
  const {
    auditListParams,
    setAuditListParams,
    getAuditableTypes,
    auditableOptions
  } = useAudit();

  const [auditableId, setAuditableId] = useState('');
  const [auditableType, setAuditableType] = useState('');
  const [auditEvent, setAuditEvent] = useState('');
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const [auditableIdError, setAuditableIdError] = useState(false);

  const onChangeField = useCallback(
    (field: 'auditable_id' | 'auditable_type', value: string) => {
      setAuditListParams({
        ...auditListParams,
        [field]: value || undefined
      });
    },
    [auditListParams, setAuditListParams]
  );

  // make onHandleClearKeyword dynamic like onChangeField
  const onHandleClearKeyword = useCallback(
    (field: 'auditable_id' | 'auditable_type') => {
      if (field === 'auditable_id') {
        setAuditableId('');
        if (auditableIdError) setAuditableIdError(false);
      }
      if (field === 'auditable_type') {
        setAuditableType('');
      }
      setAuditListParams({
        ...auditListParams,
        [field]: undefined
      });
    },
    [auditListParams, auditableIdError, setAuditListParams]
  );

  const onChangedDate = useCallback(
    (date: MaterialUiPickersDate | null, field: 'from' | 'to') => {
      const transformedDate = date ? transformerDateField(date) : undefined;
      if (field === 'to') {
        setToDate(transformedDate);
        setAuditListParams({ ...auditListParams, to_at: transformedDate });
      } else if (field === 'from') {
        setFromDate(transformedDate);
        setAuditListParams({ ...auditListParams, from_at: transformedDate });
      }
    },
    [auditListParams, setAuditListParams]
  );

  const onApplyFilter = () => {
    if (!auditableId) {
      setAuditableIdError(true);
      return;
    }
    setAuditableIdError(false);

    const cleanedParams: AuditListPayload = {
      ...(auditableId ? { auditable_id: Number(auditableId) } : {}),
      ...(auditableType ? { auditable_type: auditableType || '' } : {}),
      ...(auditEvent ? { event: auditEvent } : {}),
      ...(fromDate ? { from_at: fromDate } : {}),
      ...(toDate ? { to_at: toDate } : {})
    };

    setAuditListParams(cleanedParams);
    onHandleAppliedFilter(cleanedParams);
  };

  const onHandleErrorClose = () => {
    setAuditableIdError(false);
  };

  const onKeyPress = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      onApplyFilter();
    }
  };

  useEffect(() => {
    // Initialize from URL params only once
    const auditableIdParam = searchParams.get('auditable_id') || '';
    const auditableTypeParam = searchParams.get('auditable_type') || '';
    const eventParam = searchParams.get('event') || '';
    const from_at = searchParams.get('from_at') || null;
    const to_at = searchParams.get('to_at') || null;

    setAuditableId(auditableIdParam);
    setAuditableType(auditableTypeParam.replaceAll('\\\\', '\\'));
    setAuditEvent(eventParam);
    setFromDate(from_at || undefined);
    setToDate(to_at || undefined);
  }, [searchParams]);

  useEffect(() => {
    getAuditableTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card style={{ marginTop: 10, padding: 15 }}>
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <TextField
              fullWidth
              required
              label="Auditable ID"
              variant="outlined"
              value={auditableId}
              inputRef={auditableIdRef}
              onChange={(e) => {
                setAuditableId(e.target.value);
                onChangeField('auditable_id', e.target.value);
                if (e.target.value) setAuditableIdError(false);
              }}
              onKeyPress={onKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton
                    onClick={() => onHandleClearKeyword('auditable_id')}
                  >
                    <CloseIcon />
                  </IconButton>
                )
              }}
            />
            <PopoverNotification
              severity="error"
              textInfo="Auditable ID is required"
              isOpen={auditableIdError}
              anchorRef={auditableIdRef.current ?? undefined}
              onHandleClose={onHandleErrorClose}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="center">
            {/* <TextField
              fullWidth
              label="Auditable Type"
              variant="outlined"
              value={auditableType}
              onChange={(e) => {
                onChangeField('auditable_type', e.target.value);
                setAuditableType(e.target.value);
              }}
              onKeyPress={onKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton
                    onClick={() => onHandleClearKeyword('auditable_type')}
                  >
                    <CloseIcon />
                  </IconButton>
                )
              }}
            /> */}
            <Autocomplete
              id="auditable_type"
              options={auditableOptions || []}
              fullWidth
              getOptionLabel={(option) => option || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Auditable Type"
                  variant="outlined"
                  autoComplete="off"
                />
              )}
              value={auditableType}
              onChange={(_, val) => {
                onChangeField('auditable_type', val || '');
                setAuditableType(val || '');
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <ActionAuditFilter
            value={auditEvent}
            onChange={(event: string | undefined) => {
              setAuditEvent(event || '');
              setAuditListParams({
                ...auditListParams,
                event: event || undefined
              });
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <DatePickerRangeComponent
              fullwidth={true}
              hasNoDateToday={true}
              fromDateValue={fromDate || null}
              toDateValue={toDate || null}
              dateToLabel="Audit To"
              dateFromLabel="Audit From"
              onChangeToDate={(date) => onChangedDate(date, 'to')}
              onChangeFromDate={(date) => onChangedDate(date, 'from')}
            />
          </Box>
        </Grid>

        <Grid item xs={12} container justifyContent="center">
          <Button variant="contained" color="primary" onClick={onApplyFilter}>
            Apply Filter
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

export const AuditToolbar = memo(Component);
